class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.productsContainerEl = this.querySelector('#product-grid');
    this.paginationEl = this.querySelector('.pagination');
    this.loadingIndicator = this.querySelector('.infinite-scroll-spinner');
    this.nextPageUrl = null;

    if (!this.productsContainerEl || !this.paginationEl) return;

    // Extract next page URL immediately from the rendered pagination
    this.nextPageUrl = this.getNextPageUrl(this.paginationEl);

    if (!this.nextPageUrl) return; // Only one page, nothing to do

    this.paginationEl.style.visibility = 'hidden';

    try {
      this.observer = this.registerObserver(this.loadNextPage.bind(this));
      this.observer.observe(this.paginationEl);
    } catch (error) {
      console.error('InfiniteScroll error:', error);
    }
  }

  getNextPageUrl(paginationEl) {
    // Dawn's pagination uses aria-label="Next page" on the next link
    const ariaNext = paginationEl.querySelector('a[aria-label="Next page"]');
    if (ariaNext) return ariaNext.getAttribute('href');

    // Fallback: look for pagination__item--next class
    const classNext = paginationEl.querySelector('.pagination__item--next a');
    if (classNext) return classNext.getAttribute('href');

    // Fallback: any link containing ?page= that isn't the previous page
    const pageLinks = paginationEl.querySelectorAll('a[href*="page="]');
    const currentPage = this.getCurrentPage();
    for (const link of pageLinks) {
      const url = new URL(link.href);
      const page = parseInt(url.searchParams.get('page'));
      if (page === currentPage + 1) return link.getAttribute('href');
    }

    return null;
  }

  getCurrentPage() {
    const url = new URL(window.location.href);
    return parseInt(url.searchParams.get('page') || '1');
  }

  registerObserver(callback) {
    let running = false;
    return new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !running) {
        running = true;
        callback()
          .then(() => running = false)
          .catch(() => running = false);
      }
    }, { rootMargin: '300px' });
  }

  loadNextPage() {
    if (!this.nextPageUrl) {
      this.observer.disconnect();
      if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
      return Promise.resolve();
    }

    const urlToFetch = this.nextPageUrl;
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'flex';

    return fetch(urlToFetch)
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.text();
      })
      .then(html => {
        if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Append new product items
        const newGrid = doc.querySelector('#product-grid');
        if (newGrid) {
          newGrid.querySelectorAll('.grid__item').forEach(item => {
            this.productsContainerEl.appendChild(item);
          });
        }

        // Get next page URL from the fetched page's pagination
        const newPagination = doc.querySelector('.pagination');
        if (newPagination) {
          this.nextPageUrl = this.getNextPageUrl(newPagination);
        } else {
          this.nextPageUrl = null;
        }

        // If no more pages, stop observing
        if (!this.nextPageUrl) {
          this.observer.disconnect();
          this.paginationEl.innerHTML = '';
        }
      })
      .catch(err => {
        if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
        console.error('Infinite scroll fetch error:', err);
      });
  }
}

customElements.define('infinite-scroll', InfiniteScroll);